import { GRAIN_PATTERN_ID, SLAB_GLOSS_ID } from './material-defs-ids';
import { MATERIAL_TEXTURES, PIECE_GRAIN_WINDOW } from './board-materials';

/**
 * Dichiara una volta sola, per tutto il documento, la venatura dei PEZZI e il riflesso
 * del vetro.
 *
 * Montato in `main.tsx` come fratello di `<App />` e NON dentro l'App: `App` è uno
 * state-router a `return` anticipati che ripete a mano il proprio contorno in una
 * trentina di rami, e un punto di montaggio che un ramo può dimenticare non è un
 * punto di montaggio per qualcosa a cui ogni scacchiera fa riferimento.
 *
 * LA VENATURA VIENE DA FILE, non da path disegnati a mano. I quattro file in
 * `public/textures/` sono RASTER COTTI (vedi `MATERIAL_TEXTURES`) e qui dentro entrano
 * come `<image>`: disegnarli è un blit, e il costo non tocca il percorso per-frame.
 *
 * QUI STAVA SCRITTO che bastava mettere il filtro DENTRO il file immagine, perché «il
 * browser lo rasterizza una volta per dimensione d'uso e lo tiene in cache». Era falso:
 * Chromium mette in cache il paint record dell'SVG, non il suo raster, quindi il grafo di
 * filtri veniva rieseguito a ogni rasterizzazione — e un pattern `objectBoundingBox` su un
 * elemento che si SPOSTA, come il pezzo trascinato, si fa ri-registrare a ogni frame.
 * L'osservazione di fondo resta giusta e va tenuta: un `feTurbulence` per pezzo,
 * moltiplicato per i quaranta pezzi di una 10x10, non è ciò che il mandato mobile-first
 * consente. Solo che spostarlo dentro un file non lo eliminava: cuocerlo sì.
 *
 * ROTAZIONE. I file hanno la venatura VERTICALE, perché è così che deve stare sulla
 * scacchiera (una tavola si posa per il verso lungo). Sul pezzo va invece orizzontale:
 * ci pensa `rotate(90)` attorno al centro della finestra qui sotto.
 *
 * Ogni venatura ha DUE file, come le caselle: il pezzo chiaro prende la texture chiara
 * (acero fine), quello scuro la scura (noce marcato). È la stessa logica della lastra.
 */

/** Un pattern di venatura per pezzo: finestra sul file, ruotata in orizzontale. */
function GrainPattern({ id, href }: { id: string; href: string }) {
  const [x, y, w, h] = PIECE_GRAIN_WINDOW;
  return (
    <pattern
      id={id}
      patternUnits="objectBoundingBox"
      width="1"
      height="1"
      // `viewBox` sul pattern è il modo pulito di ritagliare E ingrandire: stabilisce un
      // viewport per il tassello, quindi la finestra sul file è una quaterna di numeri
      // invece di un gioco di x/y negativi. Serve perché il dettaglio più fine del file
      // (i pori del noce) su un pezzo da 72px cadrebbe sotto il pixel — la "velatura
      // sporca uniforme" che piece-lod.ts documenta.
      viewBox={`${x} ${y} ${w} ${h}`}
      // Il bounding box del pezzo è ellittico (FACE_SQUASH): la finestra va stirata su
      // di esso, non inscritta, o resterebbero due fasce vuote ai lati.
      preserveAspectRatio="none"
    >
      <g transform={`rotate(90 ${x + w / 2} ${y + h / 2})`}>
        <image href={href} x="0" y="0" width="1024" height="1024" />
      </g>
    </pattern>
  );
}

export default function MaterialDefs() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      // Fuori dal flusso e a dimensione nulla: esiste solo per i suoi <defs>.
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
      <defs>
        <GrainPattern id={GRAIN_PATTERN_ID.wood.light} href={MATERIAL_TEXTURES.wood.light} />
        <GrainPattern id={GRAIN_PATTERN_ID.wood.dark} href={MATERIAL_TEXTURES.wood.dark} />
        <GrainPattern id={GRAIN_PATTERN_ID.marble.light} href={MATERIAL_TEXTURES.marble.light} />
        <GrainPattern id={GRAIN_PATTERN_ID.marble.dark} href={MATERIAL_TEXTURES.marble.dark} />

        {/* ── Vetro: nessuna venatura, un riflesso ──────────────────────────
            Una banda di luce diagonale, larga e morbida. Sulla scacchiera è ciò che
            distingue il vetro dalla plastica, che non ha overlay affatto. Il resto del
            vetro lo fanno i riflessi dei pezzi (vedi `Piece`). */}
        <linearGradient id={SLAB_GLOSS_ID} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="22%" stopColor="rgba(255,255,255,0.16)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0)" />
          <stop offset="72%" stopColor="rgba(255,255,255,0.10)" />
          <stop offset="100%" stopColor="rgba(120,140,160,0.22)" />
        </linearGradient>
      </defs>
    </svg>
  );
}
